import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "attorney-web-design-colorado",
  canonicalOverride: "https://coloradowebimpressions.com/law-firm-web-design-colorado",
  metaTitle: "Law Firm Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom law firm web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Colorado Springs digital marketing", to: "/colorado-springs" },
  ],
  currentLabel: "Law Firm Web Design Colorado",
  eyebrow: "Law Firm Web Design — Colorado",
  h1: (
    <>
      Law Firm <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded law firm websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Law Firm websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first law firm sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Law Firm web design in Colorado",
    body: "Law Firm web design is the practice of building conversion-focused, mobile-first websites for law firm businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with conversion-tuned intake flows." },
    { title: "Practice area pages that rank", body: "Each practice area gets its own ranking page — not a dropdown subheading." },
    { title: "Attorney bio pages", body: "Each attorney gets a full bio with credentials, bar admissions, results, and direct contact CTA." },
    { title: "Case results & verdicts", body: "Properly disclaimed results pages that build authority without bar violations." },
    { title: "Free case evaluation forms", body: "Conversion-tested intake forms with conflict-check fields and call routing." },
    { title: "Schema and SEO at launch", body: "LegalService, Attorney, FAQPage, Review schema baked in from day one." },
  ],
  whyThisMarket: [
    { heading: "Practice-area pages outrank firm homepages", body: "Searchers type the legal problem, not the firm name. We build a ranking page for every practice area." },
    { heading: "Attorney bios drive direct hires", body: "A well-built bio page with results, credentials, and reviews converts 3x higher than the contact page." },
    { heading: "Bar compliance must be baked in", body: "Disclaimers, ad rules, and prior-results language vary by state. We ship compliant from launch." },
  ],
  relatedLinks: [
    { to: "/colorado-springs", label: "Colorado Springs digital marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a law firm website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a law firm website?", a: "Typical timeline is 4–6 weeks for a 12–20 page law firm site with full service and service-area pages. Larger multi-location law firm businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs law firm businesses?", a: "No — we build law firm sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Law Firm Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for law firm businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/attorney-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
